/**
 * Custom error classes for the Proposals library
 *
 * Provides type-safe, structured error handling with contextual information
 * for debugging and user-facing error messages.
 */

// ============================================================================
// Base Error Class
// ============================================================================

/**
 * Base error class for all proposal-related errors
 */
export class ProposalError extends Error {
  /**
   * Machine-readable error code
   */
  public readonly code: string;

  /**
   * Additional context about the error
   */
  public readonly context?: Record<string, unknown>;

  constructor(
    message: string,
    code: string,
    context?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ProposalError';
    this.code = code;
    this.context = context;

    // Maintain proper stack trace (only in V8 engines like Chrome/Node)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Serialize error for logging/debugging
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      context: this.context,
      stack: this.stack,
    };
  }
}

// ============================================================================
// API Communication Errors
// ============================================================================

/**
 * Error from API communication (network failures, HTTP errors)
 */
export class ProposalAPIError extends ProposalError {
  /**
   * HTTP status code (if available)
   */
  public readonly statusCode?: number;

  /**
   * Response body (if available)
   */
  public readonly responseBody?: unknown;

  constructor(
    message: string,
    options?: {
      code?: string;
      statusCode?: number;
      responseBody?: unknown;
      context?: Record<string, unknown>;
    }
  ) {
    super(message, options?.code || 'API_ERROR', options?.context);
    this.name = 'ProposalAPIError';
    this.statusCode = options?.statusCode;
    this.responseBody = options?.responseBody;
  }

  /**
   * Check if error is a network failure (no response)
   */
  isNetworkError(): boolean {
    return this.statusCode === undefined;
  }

  /**
   * Check if error is a client error (4xx)
   */
  isClientError(): boolean {
    return (
      this.statusCode !== undefined &&
      this.statusCode >= 400 &&
      this.statusCode < 500
    );
  }

  /**
   * Check if error is a server error (5xx)
   */
  isServerError(): boolean {
    return this.statusCode !== undefined && this.statusCode >= 500;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      statusCode: this.statusCode,
      responseBody: this.responseBody,
    };
  }
}

/**
 * Error for resource not found (404)
 */
export class ProposalNotFoundError extends ProposalAPIError {
  constructor(proposalId: number, context?: Record<string, unknown>) {
    super(`Proposal ${proposalId} not found`, {
      code: 'PROPOSAL_NOT_FOUND',
      statusCode: 404,
      context: { proposalId, ...context },
    });
    this.name = 'ProposalNotFoundError';
  }
}

/**
 * Error for permission denied (403)
 */
export class ProposalPermissionError extends ProposalAPIError {
  constructor(
    message: string,
    options?: { code?: string; context?: Record<string, unknown> }
  ) {
    super(message, {
      code: options?.code || 'PERMISSION_DENIED',
      statusCode: 403,
      context: options?.context,
    });
    this.name = 'ProposalPermissionError';
  }
}

/**
 * Error for network failures (no response received)
 */
export class ProposalNetworkError extends ProposalAPIError {
  constructor(
    message = 'Network request failed',
    context?: Record<string, unknown>
  ) {
    super(message, {
      code: 'NETWORK_ERROR',
      context,
    });
    this.name = 'ProposalNetworkError';
  }
}

// ============================================================================
// Validation Errors
// ============================================================================

/**
 * Error for validation failures
 */
export class ProposalValidationError extends ProposalError {
  /**
   * Field-specific validation errors
   */
  public readonly fieldErrors?: Record<string, string[]>;

  constructor(
    message: string,
    options?: {
      code?: string;
      fieldErrors?: Record<string, string[]>;
      context?: Record<string, unknown>;
    }
  ) {
    super(message, options?.code || 'VALIDATION_ERROR', options?.context);
    this.name = 'ProposalValidationError';
    this.fieldErrors = options?.fieldErrors;
  }

  /**
   * Check if a specific field has errors
   */
  hasFieldError(fieldName: string): boolean {
    return this.fieldErrors?.[fieldName] !== undefined;
  }

  /**
   * Get errors for a specific field
   */
  getFieldErrors(fieldName: string): string[] | undefined {
    return this.fieldErrors?.[fieldName];
  }

  toJSON() {
    return {
      ...super.toJSON(),
      fieldErrors: this.fieldErrors,
    };
  }
}

/**
 * Error for invalid action payload structure
 */
export class InvalidActionPayloadError extends ProposalValidationError {
  constructor(
    actionType: string,
    fieldErrors?: Record<string, string[]>,
    context?: Record<string, unknown>
  ) {
    super(`Invalid payload for action type: ${actionType}`, {
      code: 'INVALID_ACTION_PAYLOAD',
      fieldErrors,
      context: { actionType, ...context },
    });
    this.name = 'InvalidActionPayloadError';
  }
}

/**
 * Error for invalid proposal structure
 */
export class InvalidProposalError extends ProposalValidationError {
  constructor(
    message: string,
    fieldErrors?: Record<string, string[]>,
    context?: Record<string, unknown>
  ) {
    super(message, {
      code: 'INVALID_PROPOSAL',
      fieldErrors,
      context,
    });
    this.name = 'InvalidProposalError';
  }
}

// ============================================================================
// State Transition Errors
// ============================================================================

/**
 * Error for invalid state transitions
 */
export class ProposalStateError extends ProposalError {
  /**
   * Current state of the proposal
   */
  public readonly currentState: string;

  /**
   * Attempted operation
   */
  public readonly operation: string;

  constructor(
    message: string,
    currentState: string,
    operation: string,
    context?: Record<string, unknown>
  ) {
    super(message, 'INVALID_STATE_TRANSITION', {
      currentState,
      operation,
      ...context,
    });
    this.name = 'ProposalStateError';
    this.currentState = currentState;
    this.operation = operation;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      currentState: this.currentState,
      operation: this.operation,
    };
  }
}

/**
 * Error when trying to modify a non-pending proposal
 */
export class ProposalNotPendingError extends ProposalStateError {
  constructor(proposalId: number, currentState: string, operation: string) {
    super(
      `Cannot ${operation} proposal ${proposalId}: proposal is ${currentState}, not pending`,
      currentState,
      operation,
      { proposalId }
    );
    this.name = 'ProposalNotPendingError';
  }
}

/**
 * Error when proposal has already been resolved
 */
export class ProposalAlreadyResolvedError extends ProposalStateError {
  constructor(proposalId: number, currentState: string) {
    super(
      `Proposal ${proposalId} has already been resolved as ${currentState}`,
      currentState,
      'resolve',
      { proposalId }
    );
    this.name = 'ProposalAlreadyResolvedError';
  }
}

/**
 * Error when proposal actions have been modified by another user or process
 *
 * This occurs when attempting to approve a proposal whose actions have changed
 * since the approval page was loaded. The user should refresh to see the latest
 * actions before approving.
 */
export class ProposalStaleDataError extends ProposalAPIError {
  constructor(message: string, context?: Record<string, unknown>) {
    super(message, {
      code: 'STALE_ACTIONS',
      statusCode: 409,
      context,
    });
    this.name = 'ProposalStaleDataError';
  }
}

// ============================================================================
// Execution Errors
// ============================================================================

/**
 * Error during proposal execution
 */
export class ProposalExecutionError extends ProposalError {
  /**
   * Execution result (if available)
   */
  public readonly executionResult?: {
    total_actions: number;
    successful: number;
    failed: number;
    action_results?: unknown[];
  };

  constructor(
    message: string,
    executionResult?: {
      total_actions: number;
      successful: number;
      failed: number;
      action_results?: unknown[];
    },
    context?: Record<string, unknown>
  ) {
    super(message, 'EXECUTION_ERROR', context);
    this.name = 'ProposalExecutionError';
    this.executionResult = executionResult;
  }

  /**
   * Check if execution partially succeeded
   */
  isPartialFailure(): boolean {
    return (
      this.executionResult !== undefined &&
      this.executionResult.successful > 0 &&
      this.executionResult.failed > 0
    );
  }

  /**
   * Check if execution completely failed
   */
  isCompleteFailure(): boolean {
    return (
      this.executionResult !== undefined &&
      this.executionResult.successful === 0 &&
      this.executionResult.failed > 0
    );
  }

  toJSON() {
    return {
      ...super.toJSON(),
      executionResult: this.executionResult,
    };
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Type guard to check if error is a ProposalError
 */
export function isProposalError(error: unknown): error is ProposalError {
  return error instanceof ProposalError;
}

/**
 * Type guard to check if error is a ProposalAPIError
 */
export function isProposalAPIError(error: unknown): error is ProposalAPIError {
  return error instanceof ProposalAPIError;
}

/**
 * Type guard to check if error is a ProposalValidationError
 */
export function isProposalValidationError(
  error: unknown
): error is ProposalValidationError {
  return error instanceof ProposalValidationError;
}

/**
 * Type guard to check if error is a ProposalStaleDataError
 */
export function isProposalStaleDataError(
  error: unknown
): error is ProposalStaleDataError {
  return error instanceof ProposalStaleDataError;
}

/**
 * Extract user-friendly error message from any error
 */
export function getErrorMessage(error: unknown): string {
  if (isProposalError(error)) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unknown error occurred';
}

// ============================================================================
// User-Friendly Error Messages
// ============================================================================

/**
 * User-friendly messages for known error codes.
 * Backend may send technical messages — this map ensures users see friendly text.
 */
const ERROR_MESSAGES: Record<string, string> = {
  STALE_SELECTION:
    'Actions were updated while you were reviewing. Please try again.',
  STALE_ACTIONS:
    'Actions have changed since you loaded this page. Please refresh and try again.',
  PROPOSAL_ALREADY_RESOLVED: 'This action has already been completed.',
  PERMISSION_NOT_ASSIGNED:
    'Only the assigned reviewer can perform this action.',
  PERMISSION_NOT_IN_ORG: 'You do not have access to this organization.',
  REASSIGN_TARGET_LACKS_ACCESS:
    "They can't see some of the records this action is about, so it can't be reassigned to them.",
  NO_ACTIONS_SELECTED: 'Please select at least one action to approve.',
  INVALID_ACTION_IDS:
    'Some selected actions are no longer valid. Please refresh and try again.',
  DECRYPTION_FAILED: 'Could not load action details. Please refresh the page.',
  EXECUTION_FAILED: 'Action execution failed. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  SERVER_ERROR: 'Something went wrong on our end. Please try again later.',
  NOT_FOUND: 'The requested item could not be found.',
  BAD_REQUEST: 'Invalid request. Please check your input and try again.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  VALIDATION_FAILED: 'Invalid action data. Please check your inputs.',
  PROPOSAL_NOT_FOUND: 'This action could not be found.',
  UPDATE_FAILED: 'Could not update the action. Please refresh and try again.',
  ROLLBACK_FAILED:
    'Could not restore this version. Please refresh and try again.',
};

/** Default fallback for unknown errors */
const DEFAULT_ERROR_MESSAGE =
  'Something went wrong. Try again, and get in touch if it keeps happening.';

/**
 * Get a user-friendly error message for any proposal error.
 * Only returns mapped messages for known error codes to prevent showing raw technical errors.
 * Falls back to a generic message for unknown codes to maintain user-friendly errors.
 */
export function getUserFriendlyMessage(error: unknown): string {
  if (isProposalError(error)) {
    const mappedMessage = ERROR_MESSAGES[error.code];
    if (mappedMessage) {
      return mappedMessage;
    }
    // For unknown error codes, use the generic fallback to avoid showing technical messages
    return DEFAULT_ERROR_MESSAGE;
  }
  // For non-ProposalError cases (raw Error instances, network errors, etc.),
  // use generic fallback to avoid exposing technical details to users
  return DEFAULT_ERROR_MESSAGE;
}

/**
 * Parse API error response into appropriate error class
 */
export function parseAPIError(
  statusCode: number,
  responseBody: unknown,
  context?: Record<string, unknown>
): ProposalError {
  // Extract error message from response
  let message = 'API request failed';
  let fieldErrors: Record<string, string[]> | undefined;

  if (responseBody && typeof responseBody === 'object') {
    // Django REST Framework error format
    if ('detail' in responseBody && typeof responseBody.detail === 'string') {
      message = responseBody.detail;
    } else if (
      'error' in responseBody &&
      typeof responseBody.error === 'string'
    ) {
      message = responseBody.error;
    }

    // Field-level validation errors
    // DRF returns field errors as { field_name: ["error1", "error2"] }
    const possibleFieldErrors = {
      ...(responseBody as Record<string, unknown>),
    };
    delete possibleFieldErrors['detail'];
    delete possibleFieldErrors['error'];
    delete possibleFieldErrors['code']; // Exclude error code from field errors

    if (Object.keys(possibleFieldErrors).length > 0) {
      fieldErrors = possibleFieldErrors as Record<string, string[]>;
      message = 'Validation failed';
    }
  }

  // Extract error code from response body (if available)
  const errorCode =
    responseBody && typeof responseBody === 'object' && 'code' in responseBody
      ? (responseBody.code as string)
      : undefined;

  // Create specific error types based on status code
  switch (statusCode) {
    case 404: {
      // Extract proposal ID if available
      const proposalId = context?.proposalId as number | undefined;
      if (proposalId !== undefined) {
        return new ProposalNotFoundError(proposalId, context);
      }
      return new ProposalAPIError(message, {
        code: errorCode || 'NOT_FOUND',
        statusCode,
        responseBody,
        context,
      });
    }

    case 403:
      return new ProposalPermissionError(message, {
        code: errorCode,
        context,
      });

    case 400:
      if (fieldErrors) {
        return new ProposalValidationError(message, {
          code: errorCode || 'VALIDATION_ERROR',
          fieldErrors,
          context,
        });
      }
      return new ProposalAPIError(message, {
        code: errorCode || 'BAD_REQUEST',
        statusCode,
        responseBody,
        context,
      });

    case 409:
      // Check for stale data errors (new pattern with code field)
      if (
        responseBody &&
        typeof responseBody === 'object' &&
        'code' in responseBody
      ) {
        if (responseBody.code === 'STALE_ACTIONS') {
          return new ProposalStaleDataError(message, context);
        }
        if (responseBody.code === 'STALE_SELECTION') {
          return new ProposalAPIError(message, {
            code: 'STALE_SELECTION',
            statusCode,
            responseBody,
            context,
          });
        }
      }
      // Generic conflict - likely a state transition error
      return new ProposalAPIError(message, {
        code: 'CONFLICT',
        statusCode,
        responseBody,
        context,
      });

    case 500:
    case 502:
    case 503:
    case 504:
      return new ProposalAPIError(message, {
        code: errorCode || 'SERVER_ERROR',
        statusCode,
        responseBody,
        context,
      });

    default:
      return new ProposalAPIError(message, {
        statusCode,
        responseBody,
        context,
      });
  }
}
